'use client';

import { useState, useEffect } from 'react';
import { useApi } from '@/lib/ApiContext';
import { useNotification } from '@/lib/NotificationContext';
import { ApiService } from '@/lib/apiService';
import type { Model, Bid } from '@/lib/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { RefreshCw, Plus, Loader2, Tag, Copy, Trash2, ChevronDown, AlertCircle, Package, Layers } from 'lucide-react';
import { weiToMor, formatMor, morToWei, shortenAddress, isValidPositiveNumber } from '@/lib/utils';
import { CONTRACT_MINIMUMS } from '@/lib/constants';
import { bidPriceRangeError, useBidPriceBounds } from '@/lib/bidPriceBounds';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import ActiveModelSearch from '@/components/ActiveModelSearch';
import ConfirmDialog from './ConfirmDialog';
import FleetStatusPanel from '@/components/FleetStatusPanel';
import BidWorkflowDialog from '@/components/BidWorkflowDialog';
import type { ActiveModel } from '@/lib/activeMorOrg';
import { weiPerSecToMorPerHour } from '@/lib/activeMorOrg';

/**
 * ModelTab manages models and bids with 4 distinct sections
 */
export default function ModelTab() {
  const { apiService, walletBalance, getNetworkConfig } = useApi();
  const { success, error, warning } = useNotification();
  
  const [models, setModels] = useState<Model[]>([]);
  const [bids, setBids] = useState<Bid[]>([]);
  const [modelBidCounts, setModelBidCounts] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [bidDialogOpen, setBidDialogOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [checkingProvider, setCheckingProvider] = useState(true);
  
  // Form state for creating model + bid
  const [modelName, setModelName] = useState('');
  const [stakeMor, setStakeMor] = useState(formatMor(CONTRACT_MINIMUMS.MODEL_MIN_STAKE));
  const [feeWei, setFeeWei] = useState(CONTRACT_MINIMUMS.MODEL_REGISTRATION_FEE_WEI);
  const [tags, setTags] = useState('LLM');
  const [bidPrice, setBidPrice] = useState(CONTRACT_MINIMUMS.BID_PRICE_PER_SEC_MIN);

  // Form state for adding/changing bid (wei/sec — set by BidWorkflowDialog)
  const [newBidPrice, setNewBidPrice] = useState(CONTRACT_MINIMUMS.BID_PRICE_PER_SEC_MIN);

  // Confirmation dialogs state
  const [deleteModelConfirm, setDeleteModelConfirm] = useState<{
    open: boolean;
    model: Model | null;
  }>({ open: false, model: null });
  
  const [deleteBidConfirm, setDeleteBidConfirm] = useState<{
    open: boolean;
    bidId: string;
    modelName: string;
  }>({ open: false, bidId: '', modelName: '' });

  // Refresh trigger to notify child components when data changes
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [fleetFilter, setFleetFilter] = useState('');

  const MODEL_MIN_STAKE_MOR = formatMor(CONTRACT_MINIMUMS.MODEL_MIN_STAKE);
  const MIN_FEE_WEI = CONTRACT_MINIMUMS.MODEL_REGISTRATION_FEE_WEI;
  const bidBounds = useBidPriceBounds();
  const MIN_BID_PRICE = bidBounds.minWei;
  const BID_FEE_WEI = CONTRACT_MINIMUMS.MARKETPLACE_BID_FEE_WEI;

  useEffect(() => {
    setBidPrice((cur) => (BigInt(cur || '0') < BigInt(MIN_BID_PRICE) ? MIN_BID_PRICE : cur));
    setNewBidPrice((cur) => (BigInt(cur || '0') < BigInt(MIN_BID_PRICE) ? MIN_BID_PRICE : cur));
  }, [MIN_BID_PRICE]);

  useEffect(() => {
    if (!apiService) return;
    void loadData();
  }, [apiService]);

  // Provider status needs the wallet address — restore often sets apiService before balance arrives.
  useEffect(() => {
    if (!apiService || !walletBalance?.address) return;
    void checkProviderStatus();
  }, [apiService, walletBalance?.address]);

  const checkProviderStatus = async () => {
    if (!apiService || !walletBalance?.address) return;

    setCheckingProvider(true);
    try {
      const status = await apiService.getProviderStatus();
      setIsRegistered(status.isRegistered);
    } catch (err) {
      console.error('Provider status check failed:', err);
      setIsRegistered(false);
    } finally {
      setCheckingProvider(false);
    }
  };

  const loadData = async () => {
    if (!apiService) return;
    
    setIsLoading(true);
    try {
      const [modelsData, bidsData] = await Promise.all([
        apiService.getModels(),
        apiService.getBids()
      ]);
      // Filter for active models and bids only
      const activeModels = modelsData.filter(m => !m.IsDeleted);
      const activeBids = bidsData.filter(b => b.DeletedAt === '0' || b.DeletedAt === null || b.DeletedAt === '');
      
      setModels(activeModels);
      setBids(activeBids);

      // Fetch bid counts for all models in parallel (batched to avoid overwhelming API)
      const bidCountPromises = activeModels.map(async (model) => {
        const modelBids = await apiService.getBidsForModel(model.Id);
        return { modelId: model.Id, count: modelBids.length };
      });
      
      const bidCounts = await Promise.all(bidCountPromises);
      const bidCountMap: Record<string, number> = {};
      bidCounts.forEach(({ modelId, count }) => {
        bidCountMap[modelId] = count;
      });
      setModelBidCounts(bidCountMap);
    } catch (err) {
      error('Failed to Load', ApiService.parseError(err));
    } finally {
      setIsLoading(false);
    }
  };

  // Helper to generate a unique model ID and IPFS CID
  const generateIds = () => {
    const randomBytes = () => Math.floor(Math.random() * 256).toString(16).padStart(2, '0');
    const generateHex = () => '0x' + Array.from({ length: 32 }, randomBytes).join('');
    
    return {
      modelId: generateHex(),
      ipfsCid: generateHex()
    };
  };

  // Check if we own this model
  const isMyModel = (model: Model): boolean => {
    if (!walletBalance?.address || !model.Owner) return false;
    return model.Owner.toLowerCase() === walletBalance.address.toLowerCase();
  };

  // Get our bid for a specific model (only one bid per provider per model)
  const getMyBidForModel = (modelId: string): Bid | null => {
    if (!walletBalance?.address) return null;
    const bid = bids.find(
      bid => bid.ModelAgentId === modelId && 
             bid.Provider.toLowerCase() === walletBalance.address.toLowerCase()
    );
    return bid || null;
  };

  // Get total bid count for a model (from all providers, not just ours)
  const getTotalBidCountForModel = (modelId: string): number => {
    return modelBidCounts[modelId] ?? 0;
  };

  // Split models into 4 sections and sort by name ascending
  const fleetQ = fleetFilter.trim().toLowerCase();
  const matchFleet = (m: Model) =>
    !fleetQ ||
    m.Name.toLowerCase().includes(fleetQ) ||
    m.Id.toLowerCase().includes(fleetQ) ||
    (m.Tags || []).some((t) => t.toLowerCase().includes(fleetQ));

  const ownedModelsNoBids = models
    .filter((m) => isMyModel(m) && getMyBidForModel(m.Id) === null && matchFleet(m))
    .sort((a, b) => a.Name.localeCompare(b.Name));
  const myBidCount = models.filter((m) => getMyBidForModel(m.Id) !== null).length;

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    success('Copied', `${label} copied to clipboard`);
  };

  const handleCreateModelAndBid = async () => {
    if (!apiService || !walletBalance) return;

    const networkConfig = getNetworkConfig();
    if (!networkConfig) {
      error('Configuration Error', 'Network configuration not available');
      return;
    }

    // Validation
    if (!modelName) {
      warning('Validation Error', 'Please enter a model name');
      return;
    }

    if (!isValidPositiveNumber(stakeMor)) {
      warning('Validation Error', 'Please enter a valid stake amount');
      return;
    }

    const stakeNum = parseFloat(stakeMor);
    if (stakeNum < parseFloat(MODEL_MIN_STAKE_MOR)) {
      warning('Insufficient Stake', `Minimum model stake is ${MODEL_MIN_STAKE_MOR} MOR`);
      return;
    }

    if (!isValidPositiveNumber(feeWei)) {
      warning('Validation Error', 'Please enter a valid marketplace fee');
      return;
    }

    if (BigInt(feeWei) < BigInt(MIN_FEE_WEI)) {
      warning('Insufficient Fee', `Minimum fee is ${MIN_FEE_WEI} wei`);
      return;
    }

    if (!isValidPositiveNumber(bidPrice)) {
      warning('Validation Error', 'Please enter a valid bid price');
      return;
    }

    const createRangeError = bidPriceRangeError(bidPrice, bidBounds);
    if (createRangeError) {
      warning('Price out of range', createRangeError);
      return;
    }

    setIsCreating(true);
    try {
      // Step 1: Generate model ID and IPFS CID
      const { modelId, ipfsCid } = generateIds();
      console.log('[ModelTab] Generated Model ID:', modelId);
      console.log('[ModelTab] Generated IPFS CID:', ipfsCid);

      // Step 2: Calculate total allowance needed (model stake + marketplace bid fee)
      const modelStakeWei = morToWei(stakeMor);
      const modelFeeWei = feeWei; // model registration fee field
      const bidFeeWei = BID_FEE_WEI; // 0.3 MOR postModelBid fee
      const totalAllowanceNeeded = BigInt(modelStakeWei) + BigInt(bidFeeWei);
      const totalAllowanceStr = totalAllowanceNeeded.toString();
      
      const diamondContract = networkConfig.diamondContract;
      
      console.log('[ModelTab] User stake amount:', stakeMor, 'MOR =', modelStakeWei, 'wei');
      console.log('[ModelTab] Model registration fee:', modelFeeWei, 'wei');
      console.log('[ModelTab] Marketplace bid fee:', bidFeeWei, 'wei');
      console.log('[ModelTab] Total allowance needed (wei):', totalAllowanceStr);

      // Step 3: Check and request approval
      const currentAllowance = await apiService.getAllowance(diamondContract);
      console.log('[ModelTab] Current allowance:', currentAllowance);

      const currentAllowanceBigInt = BigInt(currentAllowance);

      if (currentAllowanceBigInt < totalAllowanceNeeded) {
        console.log('[ModelTab] Allowance insufficient, requesting approval...');
        warning(
          'Approval Required',
          `Approving ${weiToMor(totalAllowanceStr)} MOR (${weiToMor(modelStakeWei)} model stake + ${weiToMor(bidFeeWei)} bid fee)...`
        );

        const transaction = await apiService.approve(diamondContract, totalAllowanceStr);
        console.log('[ModelTab] Approval transaction:', transaction);
        
        success(
          'Approval Successful - Waiting for Confirmation',
          `Transaction submitted. Waiting for blockchain confirmation...`
        );

        console.log('[ModelTab] Waiting 5 seconds for blockchain confirmation...');
        await new Promise(resolve => setTimeout(resolve, 5000));
        
        success('Blockchain Confirmed', 'Proceeding with model creation...');
      } else {
        console.log('[ModelTab] Allowance sufficient, proceeding...');
      }

      // Step 4: Create model with retry logic
      const tagArray = tags.split(',').map(t => t.trim()).filter(t => t);
      let createdModel = null;
      let retryCount = 0;
      const maxRetries = 5;
      
      while (retryCount < maxRetries && !createdModel) {
        try {
          if (retryCount > 0) {
            const waitTime = retryCount * 3;
            console.log(`[ModelTab] Retry attempt ${retryCount}/${maxRetries} - waiting ${waitTime}s...`);
            warning('Retrying', `Waiting ${waitTime} seconds before retry ${retryCount}/${maxRetries}...`);
            await new Promise(resolve => setTimeout(resolve, waitTime * 1000));
          }
          
          createdModel = await apiService.createModel({
            id: modelId,
            name: modelName,
            ipfsID: ipfsCid,
            stake: modelStakeWei,
            fee: modelFeeWei,
            tags: tagArray,
          });
          
          console.log('[ModelTab] Model created:', createdModel);
          success('Model Created', `Model "${modelName}" created successfully`);
          break;
          
        } catch (retryError) {
          retryCount++;
          console.error(`[ModelTab] Model creation attempt ${retryCount} failed:`, retryError);
          
          if (retryCount >= maxRetries) {
            throw retryError;
          }
        }
      }

      if (!createdModel) {
        throw new Error('Failed to create model after retries');
      }

      // Step 5: Get the actual model ID from the response
      const actualModelId = createdModel.Id;
      console.log('[ModelTab] Actual Model ID from response:', actualModelId);

      // Step 6: Create bid for the new model
      console.log('[ModelTab] Creating bid for model...');
      retryCount = 0;
      let createdBid = null;

      while (retryCount < maxRetries && !createdBid) {
        try {
          if (retryCount > 0) {
            const waitTime = retryCount * 3;
            console.log(`[ModelTab] Bid retry attempt ${retryCount}/${maxRetries} - waiting ${waitTime}s...`);
            warning('Retrying Bid', `Waiting ${waitTime} seconds before retry ${retryCount}/${maxRetries}...`);
            await new Promise(resolve => setTimeout(resolve, waitTime * 1000));
          }

          createdBid = await apiService.createBid({
            modelID: actualModelId,
            pricePerSecond: bidPrice,
          });

          console.log('[ModelTab] Bid created:', createdBid);
          success('Bid Created', `Bid created with price ${bidPrice} wei/sec`);
          break;

        } catch (retryError) {
          retryCount++;
          console.error(`[ModelTab] Bid creation attempt ${retryCount} failed:`, retryError);
          
          if (retryCount >= maxRetries) {
            throw retryError;
          }
        }
      }

      // Success! Reset form and reload
      setModelName('');
      setStakeMor(MODEL_MIN_STAKE_MOR);
      setFeeWei(MIN_FEE_WEI);
      setTags('LLM');
      setBidPrice(MIN_BID_PRICE);
      setCreateDialogOpen(false);
      await loadData();
      setRefreshTrigger(prev => prev + 1);

    } catch (err) {
      error('Creation Failed', ApiService.parseError(err));
    } finally {
      setIsCreating(false);
    }
  };

  const handleBidAction = async (
    weiPerSec?: string,
    modelOverride?: Model | null
  ): Promise<boolean> => {
    const target = modelOverride || selectedModel;
    if (!apiService || !target) return false;

    const price = weiPerSec || newBidPrice;
    if (!isValidPositiveNumber(price)) {
      warning('Validation Error', 'Please enter a valid bid price');
      return false;
    }

    const rangeError = bidPriceRangeError(price, bidBounds);
    if (rangeError) {
      warning('Price out of range', rangeError);
      return false;
    }

    const updating = Boolean(getMyBidForModel(target.Id));

    setIsCreating(true);
    try {
      const networkConfig = getNetworkConfig();
      if (!networkConfig) {
        error('Configuration Error', 'Network configuration not available');
        return false;
      }

      // Check and request approval for bid fee (0.3 MOR marketplaceBidFee)
      const bidFeeWei = BID_FEE_WEI;
      const diamondContract = networkConfig.diamondContract;
      
      const currentAllowance = await apiService.getAllowance(diamondContract);
      const currentAllowanceBigInt = BigInt(currentAllowance);
      const requiredBigInt = BigInt(bidFeeWei);

      if (currentAllowanceBigInt < requiredBigInt) {
        warning('Approval Required', `Approving ${formatMor(bidFeeWei)} MOR for marketplace bid fee...`);
        
        await apiService.approve(diamondContract, bidFeeWei);
        success('Approval Successful - Waiting for Confirmation', 'Transaction submitted...');
        
        await new Promise(resolve => setTimeout(resolve, 5000));
        success('Blockchain Confirmed', `${updating ? 'Updating' : 'Creating'} bid...`);
      }

      // Create/update bid with retry
      let retryCount = 0;
      const maxRetries = 5;
      let bid = null;

      while (retryCount < maxRetries && !bid) {
        try {
          if (retryCount > 0) {
            const waitTime = retryCount * 3;
            warning('Retrying', `Waiting ${waitTime} seconds before retry ${retryCount}/${maxRetries}...`);
            await new Promise(resolve => setTimeout(resolve, waitTime * 1000));
          }

          bid = await apiService.createBid({
            modelID: target.Id,
            pricePerSecond: price,
          });

          success(
            updating ? 'Bid Updated' : 'Bid Created',
            `Bid ${updating ? 'updated' : 'created'} for ${target.Name}`
          );
          break;

        } catch (retryError) {
          retryCount++;
          if (retryCount >= maxRetries) throw retryError;
        }
      }

      setNewBidPrice(price);
      await loadData();
      setRefreshTrigger(prev => prev + 1);
      return true;

    } catch (err) {
      error(`Bid ${updating ? 'Update' : 'Creation'} Failed`, ApiService.parseError(err));
      return false;
    } finally {
      setIsCreating(false);
    }
  };

  const openBidDialog = (model: Model, editMode: boolean) => {
    setSelectedModel(model);
    setIsEditMode(editMode);
    if (editMode) {
      const existingBid = getMyBidForModel(model.Id);
      if (existingBid) {
        setNewBidPrice(existingBid.PricePerSecond);
      }
    } else {
      setNewBidPrice(MIN_BID_PRICE);
    }
    setBidDialogOpen(true);
  };

  const handleDeleteModelClick = (model: Model) => {
    const totalBids = getTotalBidCountForModel(model.Id);
    if (totalBids > 0) {
      warning('Cannot Delete', 'This model has active bids. Cannot delete.');
      return;
    }
    setDeleteModelConfirm({ open: true, model });
  };

  const handleDeleteModelConfirm = async () => {
    if (!apiService || !deleteModelConfirm.model) return;

    const model = deleteModelConfirm.model;
    setDeleteModelConfirm({ open: false, model: null });
    setIsCreating(true);
    try {
      await apiService.deleteModel(model.Id);
      success('Model Deleted', `Model "${model.Name}" has been removed`);
      await loadData();
      setRefreshTrigger(prev => prev + 1);
    } catch (err) {
      const errMsg = ApiService.parseError(err);
      if (errMsg.includes('CORS') || errMsg.includes('Access-Control-Allow-Methods')) {
        error(
          'Server Configuration Issue',
          'The API server needs to enable DELETE method in CORS configuration. Please contact the API administrator.'
        );
      } else {
        error('Delete Failed', errMsg);
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteBidConfirm = async () => {
    if (!apiService || !deleteBidConfirm.bidId) return;

    const { bidId, modelName } = deleteBidConfirm;
    setDeleteBidConfirm({ open: false, bidId: '', modelName: '' });
    setIsCreating(true);
    try {
      await apiService.deleteBid(bidId);
      success('Bid Deleted', `Your bid for "${modelName}" has been removed`);
      await loadData();
      setRefreshTrigger(prev => prev + 1);
    } catch (err) {
      const errMsg = ApiService.parseError(err);
      if (errMsg.includes('CORS') || errMsg.includes('Access-Control-Allow-Methods')) {
        error(
          'Server Configuration Issue',
          'The API server needs to enable DELETE method in CORS configuration. Please contact the API administrator.'
        );
      } else {
        error('Delete Failed', errMsg);
      }
    } finally {
      setIsCreating(false);
    }
  };

  // Collapsible model card component (mobile-friendly)
  const ModelCard = ({ 
    model, 
    bid, 
    action, 
    totalBids, 
    showDelete 
  }: { 
    model: Model; 
    bid: Bid | null; 
    action: React.ReactNode;
    totalBids?: number;
    showDelete?: boolean;
  }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
      <div className="border border-border/40 rounded-lg overflow-hidden hover:border-primary/40 transition-colors">
        {/* Collapsed View - Model Name, Model ID, Bid ID (if exists), and Tags */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full text-left p-3 hover:bg-card/50 transition-colors flex items-center justify-between gap-2"
        >
          <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
            <h3 className="font-semibold text-sm md:text-base">{model.Name}</h3>
            <span className="text-muted-foreground text-xs">-</span>
            <div className="flex items-center gap-1 flex-shrink-0">
              <span className="text-muted-foreground text-xs">modelId:</span>
              <code className="font-mono text-xs text-blue-400">{shortenAddress(model.Id, 6)}</code>
              <span
                className="h-4 w-4 flex-shrink-0 inline-flex items-center justify-center cursor-pointer hover:bg-accent rounded"
                onClick={(e) => { e.stopPropagation(); copyToClipboard(model.Id, 'Model ID'); }}
              >
                <Copy className="h-3 w-3" />
              </span>
            </div>
            {totalBids !== undefined && totalBids > 0 && (
              <>
                <span className="text-muted-foreground text-xs">-</span>
                <Badge variant="outline" className="text-xs bg-purple-500/10 border-purple-500/30 text-purple-400 flex-shrink-0">
                  {totalBids} bid{totalBids !== 1 ? 's' : ''}
                </Badge>
              </>
            )}
            {bid && (
              <>
                <span className="text-muted-foreground text-xs">-</span>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <span className="text-muted-foreground text-xs">bidId:</span>
                  <code className="font-mono text-xs text-green-400">{shortenAddress(bid.Id, 6)}</code>
                  <span
                    className="h-4 w-4 flex-shrink-0 inline-flex items-center justify-center cursor-pointer hover:bg-accent rounded"
                    onClick={(e) => { e.stopPropagation(); copyToClipboard(bid.Id, 'Bid ID'); }}
                  >
                    <Copy className="h-3 w-3" />
                  </span>
                </div>
                <Badge variant="outline" className="text-xs bg-green-500/10 border-green-500/30 text-green-400 flex-shrink-0">
                  Your Bid
                </Badge>
              </>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {model.Tags?.slice(0, 2).map((tag, i) => (
              <Badge key={i} variant="outline" className="text-xs hidden sm:inline-flex">
                {tag.toLowerCase()}
              </Badge>
            ))}
            <ChevronDown className={`h-4 w-4 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`} />
          </div>
        </button>

        {/* Expanded View - Full Details */}
        {isExpanded && (
          <div className="px-3 pt-3 pb-3 border-t border-border/20">
            <div className="flex items-start justify-between gap-3">
              {/* Left side - Model Details */}
              <div className="space-y-3 flex-1 min-w-0">
                <div className="space-y-2 text-xs">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-muted-foreground flex-shrink-0">Model ID:</span>
                      <code className="font-mono text-xs truncate">{shortenAddress(model.Id, 6)}</code>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-4 w-4 flex-shrink-0"
                        onClick={(e) => { e.stopPropagation(); copyToClipboard(model.Id, 'Model ID'); }}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-muted-foreground flex-shrink-0">Owner:</span>
                      <code className="font-mono text-xs truncate">{shortenAddress(model.Owner, 6)}</code>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-4 w-4 flex-shrink-0"
                        onClick={(e) => { e.stopPropagation(); copyToClipboard(model.Owner, 'Owner Address'); }}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <div>
                      <span className="text-muted-foreground">Stake:</span> <span className="font-medium">{formatMor(model.Stake)} MOR</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Fee:</span> <span className="font-medium">{formatMor(model.Fee)} MOR</span>
                    </div>
                    {totalBids !== undefined && (
                      <div>
                        <span className="text-muted-foreground">Bids:</span> <span className="font-medium">{totalBids}</span>
                      </div>
                    )}
                  </div>
                  {model.Tags && model.Tags.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-muted-foreground">Tags:</span>
                      {model.Tags.map((tag, i) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          <Tag className="h-3 w-3 mr-1" />
                          {tag.toLowerCase()}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Bid Info */}
                {bid && (
                  <div className="bg-green-500/5 border border-green-500/20 rounded p-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground flex-shrink-0">Your Bid ID:</span>
                      <code className="font-mono text-xs truncate text-green-400">{shortenAddress(bid.Id, 6)}</code>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-4 w-4 flex-shrink-0"
                        onClick={(e) => { e.stopPropagation(); copyToClipboard(bid.Id, 'Bid ID'); }}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                    <p className="text-sm font-semibold text-green-400 mt-1">
                      {formatMor(bid.PricePerSecond)} MOR/sec
                    </p>
                  </div>
                )}
              </div>

              {/* Right side - Action Buttons */}
              <div className="flex flex-col gap-2 flex-shrink-0">
                {bid ? (
                  // Show bid action buttons when bid exists
                  action
                ) : (
                  // Show model actions when no bid
                  <>
                    {showDelete && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteModelClick(model)}
                        disabled={isCreating}
                      >
                        <Trash2 className="h-4 w-4 sm:mr-2" />
                        <span className="hidden sm:inline">Remove</span>
                      </Button>
                    )}
                    {action}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Provider Registration Warning */}
      {!checkingProvider && !isRegistered && (
        <Card className="border-red-500/50 bg-red-500/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-400">
              <AlertCircle className="h-5 w-5" />
              Not Registered as Provider
            </CardTitle>
            <CardDescription className="text-red-300/80">
              You must register your node as a provider before you can create models and bids. 
              Please go to the <span className="font-semibold">Provider</span> tab and click "Create Provider" to register.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-foreground">Your fleet</p>
          <p className="text-xs text-muted-foreground">
            {isLoading
              ? 'Loading…'
              : `${myBidCount} active bid${myBidCount === 1 ? '' : 's'} · manage in Fleet status`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Input
            value={fleetFilter}
            onChange={(e) => setFleetFilter(e.target.value)}
            placeholder="Filter by name, id, or tag…"
            className="h-8 w-56 text-xs"
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8"
            disabled={isLoading || checkingProvider}
            onClick={() => {
              void loadData();
              void checkProviderStatus();
            }}
          >
            {isLoading ? (
              <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
            )}
            Refresh fleet
          </Button>
        </div>
      </div>

      {checkingProvider && (
        <div className="rounded-lg border border-zinc-700 bg-zinc-950/60 px-4 py-3 text-xs text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
          Loading fleet status…
        </div>
      )}

      {!checkingProvider && isRegistered && (
        <FleetStatusPanel
          refreshTrigger={refreshTrigger}
          onRefresh={() => {
            void loadData();
            setRefreshTrigger((p) => p + 1);
          }}
          onUpdateBid={async (modelId, weiPerSec) => {
            const model =
              models.find((m) => m.Id.toLowerCase() === modelId.toLowerCase()) ||
              ({
                Id: modelId,
                Name: modelId.slice(0, 10) + '…',
                Owner: walletBalance?.address || '',
              } as Model);
            return handleBidAction(weiPerSec, model);
          }}
          onDeleteBid={(bidId, modelName) => {
            setDeleteBidConfirm({ open: true, bidId, modelName });
          }}
        />
      )}

      {/* Add bid / owned-without-bid — Fleet status above is the primary bid list */}
      <Accordion
        type="multiple"
        defaultValue={[]}
        className="space-y-4"
      >
        {/* Owned models without bids */}
        {ownedModelsNoBids.length > 0 && (
          <AccordionItem value="section-3" className="border-l-4 border-l-yellow-500 border border-border/40 bg-card/50 rounded-lg overflow-hidden shadow-sm">
            <AccordionTrigger className="px-6 hover:no-underline">
              <div className="flex items-center gap-3">
                <Package className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                <div className="text-left">
                  <div className="font-semibold text-base">Your Models Without Bids ({ownedModelsNoBids.length})</div>
                  <div className="text-sm text-muted-foreground">Models you own but haven't bid on yet</div>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6">
              <div className="space-y-2">
                {ownedModelsNoBids.map((model) => {
                  const totalBids = getTotalBidCountForModel(model.Id);
                  return (
                    <ModelCard
                      key={model.Id}
                      model={model}
                      bid={null}
                      totalBids={totalBids}
                      showDelete={totalBids === 0}
                      action={
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => openBidDialog(model, false)}
                          className="w-full sm:w-auto"
                        >
                          <Plus className="h-4 w-4 sm:mr-2" />
                          <span className="hidden sm:inline">Add Bid</span>
                        </Button>
                      }
                    />
                  );
                })}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Browse marketplace via search — never dump the whole chain catalog */}
        <AccordionItem value="section-4" className="border-l-4 border-l-purple-500 border border-border/40 bg-card/50 rounded-lg overflow-hidden shadow-sm">
          <AccordionTrigger className="px-6 hover:no-underline">
            <div className="flex items-center gap-3">
              <Layers className="h-5 w-5 text-purple-500 flex-shrink-0" />
              <div className="text-left">
                <div className="font-semibold text-base">Add a bid (marketplace search)</div>
                <div className="text-sm text-muted-foreground">
                  Look up an existing model Id — avoid minting duplicates
                </div>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6 space-y-3">
            <ActiveModelSearch
              onSelect={(active: ActiveModel) => {
                const onChain = models.find((m) => m.Id.toLowerCase() === active.Id.toLowerCase());
                if (onChain) {
                  const low = active.bidDetail?.[0]?.pricePerSecond;
                  openBidDialog(onChain, false);
                  if (low) {
                    setNewBidPrice(low);
                    warning(
                      'Opened bid',
                      `${active.Name} — competing from ~${weiPerSecToMorPerHour(low).toFixed(4)} MOR/hr`
                    );
                  }
                } else {
                  warning(
                    'Not in node model list yet',
                    'Refresh fleet, or open Create Model only if this Id truly is not on-chain.'
                  );
                }
              }}
            />
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setCreateDialogOpen(true)}
                disabled={!isRegistered}
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Mint new model (last resort)
              </Button>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Empty State */}
      {!isLoading && myBidCount === 0 && models.length === 0 && (
        <Card className="border-border/40 bg-card/50">
          <CardContent className="py-12 space-y-2">
            <p className="text-center text-muted-foreground">
              No bids yet. Use marketplace search above to bid on an existing Id, or finish SecretVM
              MODELS_CONFIG then place your first bid.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Create Model & Bid Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Model & Bid</DialogTitle>
            <DialogDescription>
              Check active.mor.org first. If your model already exists, cancel and use{' '}
              <strong>Available Models → Add Bid</strong> instead. Minting duplicates clutters the marketplace.
              Each bid also charges a non-refundable {formatMor(BID_FEE_WEI)} MOR marketplace fee.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <ActiveModelSearch
              initialQuery={modelName}
              onSelect={(active: ActiveModel) => {
                setModelName(active.Name);
                const onChain = models.find((m) => m.Id.toLowerCase() === active.Id.toLowerCase());
                if (onChain) {
                  setCreateDialogOpen(false);
                  const low = active.bidDetail?.[0]?.pricePerSecond;
                  openBidDialog(onChain, false);
                  if (low) {
                    setNewBidPrice(low);
                    warning(
                      'Existing model found',
                      `Opened bid dialog for ${active.Name}. Competing from ~${weiPerSecToMorPerHour(low).toFixed(4)} MOR/hr.`
                    );
                  } else {
                    warning('Existing model found', `Opened bid dialog for ${active.Name}. Prefer bidding over minting.`);
                  }
                } else {
                  warning(
                    'Model exists on active.mor.org',
                    'Use Available Models (or refresh) and Add Bid on that Id — do not mint a duplicate unless you need new tags (e.g. tee).'
                  );
                }
              }}
            />
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label htmlFor="modelName">Model Name *</Label>
                <Input
                  id="modelName"
                  placeholder="Hermes-2-Theta-Llama-3-8B"
                  value={modelName}
                  onChange={(e) => setModelName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="modelStake">Model Stake (MOR)</Label>
                <Input
                  id="modelStake"
                  type="text"
                  placeholder={MODEL_MIN_STAKE_MOR}
                  value={stakeMor}
                  onChange={(e) => setStakeMor(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">Minimum: {formatMor(CONTRACT_MINIMUMS.MODEL_MIN_STAKE)} MOR</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="modelFee">Model registration fee (wei)</Label>
                <Input
                  id="modelFee"
                  type="text"
                  placeholder={MIN_FEE_WEI}
                  value={feeWei}
                  onChange={(e) => setFeeWei(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  On-chain model fee field (not the {formatMor(BID_FEE_WEI)} MOR bid fee)
                  {feeWei && parseFloat(feeWei) > 0 && (
                    <span className="ml-2 text-blue-400">≈ {formatMor(feeWei)} MOR</span>
                  )}
                </p>
              </div>
              <div className="space-y-2 col-span-2">
                <Label htmlFor="tags">Tags (comma-separated)</Label>
                <Input
                  id="tags"
                  placeholder="LLM, tee"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">Include <code>tee</code> for SecretVM TEE offerings.</p>
              </div>
              <div className="space-y-2 col-span-2">
                <Label htmlFor="bidPrice">Bid Price Per Second (wei)</Label>
                <Input
                  id="bidPrice"
                  type="text"
                  placeholder={MIN_BID_PRICE}
                  value={bidPrice}
                  onChange={(e) => setBidPrice(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  On-chain range: {MIN_BID_PRICE}
                  {bidBounds.maxWei ? `–${bidBounds.maxWei}` : ''} wei/sec · bid fee:{' '}
                  {formatMor(BID_FEE_WEI)} MOR (non-refundable)
                </p>
              </div>
            </div>
            <Button
              onClick={handleCreateModelAndBid}
              disabled={isCreating}
              className="w-full"
            >
              {isCreating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating Model & Bid...
                </>
              ) : (
                'Create Model & Bid'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bid workflow: market → MOR/hr → backend MODELS_CONFIG → copy secrets */}
      <BidWorkflowDialog
        open={bidDialogOpen}
        onOpenChange={(open) => {
          setBidDialogOpen(open);
          if (!open) {
            setSelectedModel(null);
            setIsEditMode(false);
          }
        }}
        model={selectedModel}
        editMode={isEditMode}
        existingWeiPerSec={
          isEditMode && selectedModel
            ? getMyBidForModel(selectedModel.Id)?.PricePerSecond
            : newBidPrice
        }
        isSubmitting={isCreating}
        onSubmitBid={(wei) => handleBidAction(wei)}
        onFinished={() => {
          void loadData();
          setRefreshTrigger((p) => p + 1);
        }}
      />

      {/* Delete Model Confirmation Dialog */}
      <ConfirmDialog
        open={deleteModelConfirm.open}
        onOpenChange={(open) => setDeleteModelConfirm({ ...deleteModelConfirm, open })}
        onConfirm={handleDeleteModelConfirm}
        title="Delete Model"
        description={`Are you sure you want to delete model "${deleteModelConfirm.model?.Name}"? This action cannot be undone.`}
        confirmText="Delete Model"
        cancelText="Cancel"
        variant="destructive"
      />

      {/* Delete Bid Confirmation Dialog */}
      <ConfirmDialog
        open={deleteBidConfirm.open}
        onOpenChange={(open) => setDeleteBidConfirm({ ...deleteBidConfirm, open })}
        onConfirm={handleDeleteBidConfirm}
        title="Delete Bid"
        description={`Are you sure you want to delete your bid for "${deleteBidConfirm.modelName}"? This action cannot be undone.`}
        confirmText="Delete Bid"
        cancelText="Cancel"
        variant="destructive"
      />
    </div>
  );
}
